/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {GlobalSearchCriteriaService} from '../services/global-search-criteria.service';
import {GlobalSearchCriterionFilterModel} from '../models/global-search-criterion-filter.model';
import {Subscription} from 'rxjs';
import {DropdownCriteriaSelectionState} from './state/dropdown-criteria-selection.state';
import {StatefulComponent} from '../../stateful/stateful.component';
import {DropdownCriterionSelectionModel} from './models/dropdown-criterion-selection.model';

@Component({
    selector: 'app-dropdown-criteria-selection',
    templateUrl: './dropdown-criteria-selection.component.html',
    styleUrls: [
        '../shared/styles/select-dropdown.css',
        './dropdown-criteria-selection.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DropdownCriteriaSelectionComponent extends StatefulComponent<DropdownCriteriaSelectionState> implements OnInit, OnDestroy {
    @Input() searchCriteriaChange: EventEmitter<void>;
    @Output() searchCriteriaReset = new EventEmitter<void>();
    @Output() dropdownIsOpenEvent = new EventEmitter<boolean>();

    @ViewChild('dropdown') dropdown: NgbDropdown;

    dropdownDefaultButtonText = 'Please select search variable';
    selectedDropdownOption: string[] = [];

    private userMouseOverEntryCode: string;
    private subscriptions = new Subscription();

    constructor(private globalSearchCriteriaService: GlobalSearchCriteriaService) {
        super();
    }

    ngOnInit(): void {
        this.subscriptions.add(this.searchCriteriaChange?.subscribe(() => {
            this.clearContextInDropdown();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    originalOrder(): number {
        return 0;
    }

    get dropdownData(): Map<string, DropdownCriterionSelectionModel> {
        return this.globalSearchCriteriaService.getDropdownDataBySearchCriteriaCode().criteria;
    }

    getSubDropdownData(code: string): Map<string, DropdownCriterionSelectionModel> {
        return this.dropdownData.get(code)?.subDropdown;
    }

    handleSubDropdownClick(entryCode: string,
                           subEntryCode: string,
                           subEntryValue: DropdownCriterionSelectionModel): void {
        const subEntryData = this.globalSearchCriteriaService.getCriterionFromSearchCriteria(entryCode, subEntryCode);

        if (subEntryData?.disabled === false) {
            this.setSelectedOption(entryCode, subEntryCode, subEntryValue);

            this.globalSearchCriteriaService.enableCriterion({
                criterionContext: this.state?.entryCode,
                criterion: this.state?.subEntryCode,
                criterionFilterInputType: this.state?.inputTypeCode,
                criterionDynamicContext: this.state?.dynamicContext
            });
            this.globalSearchCriteriaService.disableCriterion({
                criterion: subEntryCode,
                criterionContext: entryCode,
                criterionFilterInputType: subEntryValue?.inputTypeSearch,
                criterionDynamicContext: subEntryValue?.dynamicDropdownContext
            });
            this.storeState({
                entryCode: entryCode,
                subEntryCode: subEntryCode,
                inputTypeCode: subEntryValue.inputTypeSearch,
                dynamicContext: subEntryValue.dynamicDropdownContext
            });

            this.searchCriteriaReset.emit();
        }
    }

    dropdownItemDataCy(itemValue: string, isButton?: boolean): string {
        if (itemValue != null) {
            if (isButton) {
                return `dropdown-item-button-${itemValue.toLowerCase()
                        .replace(new RegExp(/\s/g), '-')}`;
            } else {
                return `dropdown-item-text-${itemValue.toLowerCase()
                        .replace('(', '')
                        .replace(')', '')
                        .replace(new RegExp(/\s/g), '-')}`;
            }
        }
    }

    openDropdownSubMenu(entryCode: string): void {
        this.userMouseOverEntryCode = entryCode;
    }

    closeDropdownSubMenu(): void {
        this.userMouseOverEntryCode = undefined;
    }

    isAtSameLevel(entryCode: string): boolean {
        return this.userMouseOverEntryCode === entryCode;
    }

    hasDropdownItemDataToShow(code: string): boolean {
        return this.dropdownData.get(code).subDropdown?.size > 0;
    }

    showSubDropdownMenu(entryCode: string): boolean {
        return this.isAtSameLevel(entryCode) && this.hasDropdownItemDataToShow(entryCode);
    }

    getButtonPlaceholder(): string[] {
        return this.selectedDropdownOption.length ? this.selectedDropdownOption : [this.dropdownDefaultButtonText];
    }

    hasSelectedOption(): boolean {
        return !!this.selectedDropdownOption.length;
    }

    dropdownIsOpen(isOpen: boolean): void {
        this.dropdownIsOpenEvent.emit(isOpen);
    }

    private setSelectedOption(entryCode: string,
                              subEntryCode: string,
                              subEntryValue: DropdownCriterionSelectionModel): void {
        const filter: GlobalSearchCriterionFilterModel = {
            criterion: subEntryCode,
            criterionContext: entryCode,
            criterionDynamicContext: subEntryValue?.dynamicDropdownContext,
            criterionFilterInputType: subEntryValue?.inputTypeSearch
        };
        this.globalSearchCriteriaService.setSelectedSearchCriterionFilter(filter);
        this.globalSearchCriteriaService.setSelectedSearchCriterionName(subEntryValue?.name);
        this.selectedDropdownOption = [
            this.dropdownData.get(entryCode).name,
            this.dropdownData.get(entryCode)
                    ?.subDropdown
                    .get(subEntryCode)?.name
        ];
        this.dropdown.close();
    }

    private clearContextInDropdown(): void {
        this.storeState({
            entryCode: null,
            subEntryCode: null
        });
        this.selectedDropdownOption = [];
        this.globalSearchCriteriaService.setSelectedSearchCriterionFilter(null);
    }
}
